"use client";

import Link from "next/link";
import styled from "styled-components";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { formatMoney, formatPercent } from "@/utils/format";
import { ChevronRight, Lightbulb, Package, Truck } from "lucide-react";
import type { Connection, DashboardSummary, OzonDashboardSummary } from "./types";

interface MarketplaceCardProps {
  marketplace: "wildberries" | "ozon";
  connection?: Connection;
  summary?: DashboardSummary | OzonDashboardSummary;
  isLoading?: boolean;
}

const marketplaceConfig = {
  wildberries: {
    name: "Wildberries",
    shortName: "WB",
  },
  ozon: {
    name: "Ozon",
    shortName: "OZ",
  },
};

const MarketplaceBlock = styled.div``;

const HeaderWrapper = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
`;

const TitleWrapper = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 16px;
`;

const LetterLogo = styled.div<{ $marketplace: 'wildberries' | 'ozon'; }>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: 32px;
  height: 32px;
  color: white;
  font-weight: 800;
  font-size: 12px;
  border-radius: 9px;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;

  ${({ $marketplace }) =>
    $marketplace === 'wildberries'
      ? `background: var(--color-wb); color: var(--color-text-inverse);`
      : `background: var(--color-ozon); color: var(--color-text-inverse);`}
`;

const EmptyContent = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 32px 0;
  text-align: center;
`;

const EmptyText = styled.p`
  font-size: 14px;
  color: var(--color-text-secondary);
  margin: 0 0 16px 0;
`;

const ContentWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
`;

const StatusRow = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  font-size: 14px;
`;

const StatusIndicator = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`;

const StatusDot = styled.span<{ $active: boolean }>`
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background-color: ${({ $active }) => ($active ? "var(--color-success)" : "var(--color-text-tertiary)")};
`;

const StatusText = styled.span`
  color: var(--color-text-secondary);
`;

const SyncText = styled.span`
  font-size: 12px;
  color: var(--color-text-tertiary);
`;

const MetricsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 12px;
`;

const MetricBox = styled.div`
  background-color: var(--color-bg-page);
  border-radius: 8px;
  padding: 8px;
`;

const MetricLabel = styled.p`
  font-size: 12px;
  color: var(--color-text-secondary);
  margin: 0;
`;

const MetricValue = styled.p`
  font-size: 14px;
  font-weight: 600;
  color: var(--color-text-primary);
  margin: 0;
`;

const NavigationRow = styled.div`
  display: flex;
  gap: 8px;
  padding-top: 8px;
`;

const NavLink = styled(Link)`
  flex: 1;
`;

const NavButton = styled(Button)`
  width: 100%;
  font-size: 12px;
`;

const LoadingContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const LoadingGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 16px;
  padding-top: 16px;
`;

function isWbSummary(summary: DashboardSummary | OzonDashboardSummary): summary is DashboardSummary {
  return "for_pay" in summary;
}

function formatRelativeTime(dateString: string): string {
  const date = new Date(dateString);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMins = Math.floor(diffMs / 60000);
  const diffHours = Math.floor(diffMins / 60);
  const diffDays = Math.floor(diffHours / 24);

  if (diffMins < 1) return "только что";
  if (diffMins < 60) return `${diffMins} мин. назад`;
  if (diffHours < 24) return `${diffHours} ч. назад`;
  if (diffDays < 7) return `${diffDays} дн. назад`;
  return date.toLocaleDateString("ru-RU");
}

interface MetricItemProps {
  label: string;
  value: string;
}

function MetricItem({ label, value }: MetricItemProps) {
  return (
    <MetricBox>
      <MetricLabel>{label}</MetricLabel>
      <MetricValue>{value}</MetricValue>
    </MetricBox>
  );
}

export function MarketplaceCard({ marketplace, connection, summary, isLoading }: MarketplaceCardProps) {
  const config = marketplaceConfig[marketplace];

  if (!connection) {
    return (
      <MarketplaceBlock>
        <CardHeader>
          <TitleWrapper>
            <LetterLogo $marketplace={marketplace}>
              {config.shortName}
            </LetterLogo>
            {config.name}
          </TitleWrapper>
        </CardHeader>
        <CardContent>
          <EmptyContent>
            <EmptyText>Подключите {config.name} для просмотра аналитики продаж</EmptyText>
            <Link href="/settings">
              <Button variant="outline" size="sm">
                Подключить
                <ChevronRight size={16} />
              </Button>
            </Link>
          </EmptyContent>
        </CardContent>
      </MarketplaceBlock>
    );
  }

  if (isLoading) {
    return (
      <MarketplaceBlock>
        <CardHeader>
          <HeaderWrapper>
            <Skeleton width={128} height={24} />
            <Skeleton width={24} height={24} />
          </HeaderWrapper>
        </CardHeader>
        <CardContent>
          <LoadingContent>
            <Skeleton width={192} height={16} />
            <Skeleton width={128} height={16} />
            <LoadingGrid>
              {[1, 2, 3, 4].map((i) => (
                <Skeleton key={i} height={48} />
              ))}
            </LoadingGrid>
          </LoadingContent>
        </CardContent>
      </MarketplaceBlock>
    );
  }

  let profit = "—";
  let salesCount = 0;
  let salesLabel = "Продажи";
  let thirdMetric = "—";
  let thirdMetricLabel = "Выкуп";
  let roi = "—";

  if (summary) {
    if (isWbSummary(summary)) {
      profit = formatMoney(summary.profit_before_tax);
      salesCount = summary.sales_count;
      salesLabel = "Продажи";
      thirdMetric = formatPercent(summary.redemption_rate);
      thirdMetricLabel = "Выкуп";
      roi = formatPercent(summary.profitability_percent);
    } else {
      profit = formatMoney(summary.profit_before_tax);
      salesCount = summary.delivered_units || 0;
      salesLabel = "Продажи";
      const returnRate = parseFloat(summary.return_rate) || 0;
      const redemptionRate = 100 - returnRate;
      thirdMetric = formatPercent(redemptionRate.toFixed(1));
      thirdMetricLabel = "Выкуп";
      roi = formatPercent(summary.roi);
    }
  }

  const lastSyncText = connection.last_sync_at
    ? formatRelativeTime(connection.last_sync_at)
    : "не синхронизирован";

  return (
    <MarketplaceBlock>
      <CardHeader>
        <HeaderWrapper>
          <TitleWrapper>
            <LetterLogo $marketplace={marketplace}>
              {config.shortName}
            </LetterLogo>
            {connection.store_name || config.name}
          </TitleWrapper>
          <Link href={`/${marketplace === "ozon" ? "ozon" : "wb"}/${connection.uuid}/products`}>
            <Button variant="ghost" size="icon">
              <ChevronRight size={16} />
            </Button>
          </Link>
        </HeaderWrapper>
      </CardHeader>
      <CardContent>
        <ContentWrapper>
          <StatusRow>
            <StatusIndicator>
              <StatusDot $active={connection.is_active} />
              <StatusText>{connection.is_active ? "Активен" : "Неактивен"}</StatusText>
            </StatusIndicator>
            <SyncText>Обновлено: {lastSyncText}</SyncText>
          </StatusRow>

          <MetricsGrid>
            <MetricItem label="Прибыль" value={profit} />
            <MetricItem label={salesLabel} value={salesCount.toLocaleString("ru-RU")} />
            <MetricItem label={thirdMetricLabel} value={thirdMetric} />
            <MetricItem label="ROI" value={roi} />
          </MetricsGrid>

          <NavigationRow>
            <NavLink href={`/${marketplace === "ozon" ? "ozon" : "wb"}/${connection.uuid}/insights`}>
              <NavButton variant="outline" size="sm">
                <Lightbulb size={12} />
                Инсайты
              </NavButton>
            </NavLink>
            <NavLink href={`/${marketplace === "ozon" ? "ozon" : "wb"}/${connection.uuid}/products`}>
              <NavButton variant="outline" size="sm">
                <Package size={12} />
                Товары
              </NavButton>
            </NavLink>
            <NavLink href={`/${marketplace === "ozon" ? "ozon" : "wb"}/${connection.uuid}/supplies`}>
              <NavButton variant="outline" size="sm">
                <Truck size={12} />
                Поставки
              </NavButton>
            </NavLink>
          </NavigationRow>
        </ContentWrapper>
      </CardContent>
    </MarketplaceBlock>
  );
}